/**
 * RuntimePanel — Connection status, backend version, and database info.
 *
 * API wired: GET /health → { status, version, runtime_mode, database_kind, uptime_seconds }
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { SettingsSection } from '@seitiate/settings-ui';
import { API_V1, SEITIATE_ENGINE_URL } from '@/lib/env';
import { buildEngineHeaders } from '@/lib/auth';

interface HealthResponse {
    status: string;
    version?: string;
    runtime_mode?: string;
    database_kind?: string;
    uptime_seconds?: number;
}

function formatUptime(seconds: number): string {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export const RuntimePanel: React.FC = () => {
    const [health, setHealth] = useState<HealthResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchHealth = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await fetch(`${API_V1}/health`, { headers: buildEngineHeaders() });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            setHealth(await resp.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Backend unreachable');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchHealth();
    }, [fetchHealth]);

    const connected = health?.status === 'ok';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3
                    className="text-xs uppercase tracking-[0.2em]"
                    style={{ color: 'var(--theme-text-secondary)', fontFamily: 'var(--theme-font-mono)' }}
                >
                    Runtime
                </h3>
                <div className="flex items-center gap-2">
                    <span
                        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{
                            color: 'var(--theme-accent-primary)',
                            background: 'color-mix(in srgb, var(--theme-accent-primary) 10%, transparent)',
                            fontFamily: 'var(--theme-font-mono)',
                            borderRadius: 'var(--theme-radius-sm)',
                        }}
                    >
                        Read-only
                    </span>
                    <button
                        type="button"
                        onClick={() => void fetchHealth()}
                        disabled={loading}
                        className="p-1 rounded transition-colors"
                        style={{ color: 'var(--theme-text-muted)', borderRadius: 'var(--theme-radius-sm)' }}
                        title="Refresh"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            <SettingsSection title="Connection">
                {loading && !health ? (
                    <div
                        className="flex items-center gap-2 py-4 text-xs"
                        style={{ color: 'var(--theme-text-muted)', fontFamily: 'var(--theme-font-mono)' }}
                    >
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking backend…
                    </div>
                ) : error ? (
                    <div className="space-y-2">
                        <div
                            className="flex items-center gap-2 text-xs"
                            style={{ color: 'var(--theme-danger)', fontFamily: 'var(--theme-font-mono)' }}
                        >
                            <XCircle className="w-3.5 h-3.5" />
                            Disconnected
                        </div>
                        <div
                            className="text-[10px]"
                            style={{ color: 'var(--theme-text-muted)', fontFamily: 'var(--theme-font-mono)' }}
                        >
                            {error}
                        </div>
                    </div>
                ) : health ? (
                    <div className="space-y-2">
                        <div
                            className="flex items-center gap-2 text-xs"
                            style={{
                                color: connected ? 'var(--theme-success)' : 'var(--theme-warning)',
                                fontFamily: 'var(--theme-font-mono)',
                            }}
                        >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Connected
                        </div>
                        <InfoRow label="Backend URL" value={SEITIATE_ENGINE_URL} />
                        {health.version && <InfoRow label="Version" value={health.version} />}
                        {health.runtime_mode && <InfoRow label="Runtime mode" value={health.runtime_mode} />}
                        {health.database_kind && <InfoRow label="Database" value={health.database_kind} />}
                        {typeof health.uptime_seconds === 'number' && (
                            <InfoRow label="Uptime" value={formatUptime(health.uptime_seconds)} />
                        )}
                    </div>
                ) : null}
            </SettingsSection>

            <SettingsSection title="Sidecar" defaultExpanded={false}>
                <div
                    className="text-[10px] px-3 py-2 rounded"
                    style={{
                        color: 'var(--theme-text-muted)',
                        background: 'color-mix(in srgb, var(--theme-surface-tertiary) 50%, transparent)',
                        border: '1px solid var(--theme-surface-tertiary)',
                        fontFamily: 'var(--theme-font-mono)',
                        borderRadius: 'var(--theme-radius-sm)',
                    }}
                >
                    Sidecar process monitoring is available in the desktop app (Electron) only.
                </div>
            </SettingsSection>
        </div>
    );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex items-baseline justify-between text-[10px]" style={{ fontFamily: 'var(--theme-font-mono)' }}>
        <span style={{ color: 'var(--theme-text-muted)' }}>{label}</span>
        <span style={{ color: 'var(--theme-text-primary)' }}>{value}</span>
    </div>
);
